// Core
import { Fragment, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

// Chakra
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';

// Components
import { GiveawaysActiveIcon } from 'components/Icons/Icons';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Card from 'components/Card/Card';
import Field from 'components/Fields';
import ErrorCard from 'components/Errors';
import SuccessCard from '../../../../components/Success';
import { Controller } from 'react-hook-form';
import Attention from '../../../../components/Modals/Attention';
import usePermissionStatusByKey from '../../../../hooks/usePermissionStatusByKey';
import StationInput from './StationInput';
import { priceCodeOptions } from '../../../../constants/prices';

const NextBasePriceContent = ({
  form,
  onSubmit,
  isSubmitting,
  errorCard,
  isNew,
  handleDelete,
}) => {
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const history = useHistory();
  const handleCancelClick = useCallback(() => {
    history.push('/base-prices/next-base-prices');
  }, [history]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = form;

  const isCreateBasePrice = usePermissionStatusByKey(
    'POST:/admin/next-base-prices',
  );
  const isUpdateBasePrice = usePermissionStatusByKey(
    'PATCH:/admin/next-base-prices/:id',
  );
  const isDeletebasePrice = usePermissionStatusByKey(
    'DELETE:/admin/next-base-prices/:id',
  );

  return (
    <Box zIndex="9" position="relative">
      {!isNew ? (
        <Breadcrumbs
          icon={GiveawaysActiveIcon}
          target="Scheduled Base Prices"
          url="#/base-prices/next-base-prices"
          current="Scheduled Base Price"
          variant="standart"
        />
      ) : (
        <Breadcrumbs
          icon={GiveawaysActiveIcon}
          target="Scheduled Base Prices"
          url="#/base-prices/next-base-prices"
          current="New Scheduled Base Price"
          variant="standart"
        />
      )}

      <form onSubmit={onSubmit}>
        <Flex
          gap="16px"
          position="absolute"
          mt="-92px"
          right="0"
          alignItems="center"
        >
          {isNew && (
            <Button
              variant="ghost-md"
              minW="120px"
              onClick={() => handleCancelClick()}
            >
              Cancel
            </Button>
          )}

          {!isNew && isDeletebasePrice && (
            <Button variant="ghost-md" w="120px" onClick={onOpenDelete}>
              Delete
            </Button>
          )}

          {(!isNew ? isUpdateBasePrice : isCreateBasePrice) && (
            <Button
              isLoading={isSubmitting}
              variant="submit-md"
              w="120px"
              type="submit"
            >
              {!isNew ? 'Confirm' : 'Create'}
            </Button>
          )}
        </Flex>

        {errorCard && <ErrorCard error={errorCard} maxW="840px" />}
        {message && <SuccessCard message={message} maxW={'100%'} />}
        {error && <ErrorCard error={error} maxW={'100%'} />}

        <Box maxW="840px">
          <Text variant="title-lg" mt="40px" mb="14px">
            Base Price info
          </Text>

          <Card padding="24px" mb="40px">
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <FormControl variant="floating" id="code" mb="24px">
                  <Select
                    value={field.value || ''}
                    placeholder="Select fuel type"
                    className={!errors.code ? 'input-valid' : 'input-invalid'}
                    onChange={(e) => {
                      const selectedValue = e.target.value;

                      field.onChange(selectedValue);

                      const selectedOption = priceCodeOptions.find(
                        (opt) => opt.value === selectedValue,
                      );

                      const currentName = getValues('name');

                      if (!currentName && selectedOption) {
                        setValue('name', selectedOption.label, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }}
                  >
                    {priceCodeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {`${option.value} - ${option.label}`}
                      </option>
                    ))}
                  </Select>

                  <FormLabel
                    htmlFor="code"
                    className={!errors.code ? 'input-valid' : 'input-invalid'}
                  >
                    Fuel Type
                  </FormLabel>

                  {errors.code && (
                    <FormHelperText className="input-error">
                      Select a fuel type
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <Field
                  id="price"
                  label="Price"
                  type="number"
                  value={field.value}
                  isValid={!errors.price}
                  onChange={field.onChange}
                  mb="24px"
                />
              )}
            />

            <Controller
              name="stationId"
              control={control}
              render={({ field }) => (
                <StationInput
                  isValid={!errors.stationId}
                  isDisabled={false}
                  onChange={field.onChange}
                  initialInputValue={form.getValues('station')?.name}
                  value={field.value}
                  wrapperProps={{
                    mb: '24px',
                  }}
                />
              )}
            />
          </Card>
        </Box>
      </form>
      <Attention
        variant="remove"
        handle={handleDelete}
        isOpen={isOpenDelete}
        onOpen={onOpenDelete}
        onClose={onCloseDelete}
      />
    </Box>
  );
};

export default NextBasePriceContent;
